import React, { useState, Fragment, useMemo, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { Select } from 'mui-rff';
import {
  Box,
  Grid,
  MenuItem,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
//
import {
  createFormFields,
  IntegrationTypeRadioControls,
  PaymentTypeRadioControls,
} from './fields';
import { getInitialForm } from 'components/form/publisher/utils';
import ButtonSubmit from 'components/common/ButtonSubmit';
import FilesDropZone from 'components/common/DropZone';
import Loader from 'components/common/Loader';
import {
  MAX_FILE_SIZE,
  SSP_LOGO_EXTENSIONS,
  SSP_LOGO_MAX_RESOLUTION,
} from '../../../../constants';
import { normalizeBoolean } from 'utils/normalizers';
import { handleApiError } from 'utils/common';

import {
  useGetPublisherDetailsQuery,
  useUpdatePublisherMutation,
} from '../../../../store/api/publisher.api';
import { useGetEndpointsDropdownQuery } from '../../../../store/api/endpoint.api';

const GridFlexEnd = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'flex-end',
}));

export default function General({ id, editable }) {
  const isUpdate = useMemo(() => !!id, [id]);
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { data: generalInfo = getInitialForm(), isSuccess: loadedPublisher } =
    useGetPublisherDetailsQuery(id, { skip: !id });
  const { data: endpoints = [], isSuccess: loadedEndpoints } =
    useGetEndpointsDropdownQuery();
  const [updatePublisher] = useUpdatePublisherMutation();
  const [files, setFiles] = useState([]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        const formData = new FormData();
        formData.append('data', JSON.stringify(values));
        for (const file of files) {
          formData.append(file.id, file);
        }

        let publisherId = id;
        const data = { payload: formData };
        if (isUpdate) {
          data.id = id;
        }
        const result = await updatePublisher(data).unwrap();
        if (isUpdate) {
          navigate('/publishers');
        } else {
          publisherId = result.pub.id;
          navigate(`/publishers/${result.pub.id}/edit`);
        }
        enqueueSnackbar(
          `(${publisherId}) Publisher ${isUpdate ? 'updated' : 'created'} successfully`,
          { variant: 'success' },
        );
      } catch (e) {
        handleApiError(e);
      }
    },
    [files, navigate, isUpdate, updatePublisher, enqueueSnackbar, id],
  );

  const handleUploadFile = (files) => {
    const confirm = () => {
      const uploadedFiles = [];

      files.forEach((file) => {
        const _URL = window.URL || window.webkitURL;
        const img = new Image();
        img.onload = () => {
          file.width = img.width;
          file.height = img.height;
          file.resolution = `${img.width}x${img.height}`;
          file.createdAt = new Date();

          uploadedFiles.push(file);

          if (files.length === uploadedFiles.length) {
            bulkUploadCreatives(uploadedFiles, 'image');
          }
        };

        img.src = _URL.createObjectURL(file);
      });
    };

    if (files.length === 1) {
      confirm();
    }
  };

  const bulkUploadCreatives = (data, type) => {
    const uploadedFiles = Array.isArray(data) ? data : [data];
    const uploaded = [];

    for (const file of uploadedFiles) {
      file.id = type;
      uploaded.push(file);
    }

    setFiles(uploaded);
  };
  if (!loadedEndpoints || (isUpdate && !loadedPublisher)) {
    return (
      <CircularProgress
        color='primary'
        size='4rem'
        sx={{ mx: 'auto', my: '2rem', display: 'block' }}
      />
    );
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={generalInfo}
      render={({ handleSubmit, form, submitting, values }) => (
        <form onSubmit={handleSubmit} noValidate>
          <Loader isPending={submitting} />
          <Paper style={{ padding: 16 }} component={Grid} container>
            <GridFlexEnd size={{ xs: 12 }}>
              <FilesDropZone
                extensions={SSP_LOGO_EXTENSIONS}
                maxFileSize={MAX_FILE_SIZE}
                maxFileResolution={SSP_LOGO_MAX_RESOLUTION}
                onUploadFile={handleUploadFile}
                isMulti={false}
                allowUploadThroughArchive
                form={form}
                state={values}
                disabled={!editable}
              />
            </GridFlexEnd>

            <Grid
              container
              size={{ xs: 12 }}
              alignItems='flex-start'
              spacing={2}
            >
              {values &&
                createFormFields(values, isUpdate, !editable).map(
                  (item, idx) => (
                    <GridFlexEnd size={{ xs: item.size }} key={idx}>
                      {item.field}
                      {item.isCopy && item.name ? (
                        <CopyToClipboard
                          text={values[item.name]}
                          onCopy={() =>
                            enqueueSnackbar('Copied!', {
                              variant: 'success',
                            })
                          }
                        >
                          <IconButton
                            size='small'
                            color='primary'
                            aria-label='copy'
                          >
                            <FileCopyOutlinedIcon fontSize='small' />
                          </IconButton>
                        </CopyToClipboard>
                      ) : null}
                    </GridFlexEnd>
                  ),
                )}
            </Grid>
            <Grid
              size={{ xs: 12 }}
              alignItems='start'
              mt='1rem'
              container
              spacing={1}
            >
              <Grid size={{ xs: 4 }} container>
                <Grid size={{ xs: 12 }}>
                  <Field
                    component={IntegrationTypeRadioControls}
                    disabled={!editable}
                    name='isGateway'
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {normalizeBoolean(values.isGateway) && (
                    <Field
                      component={PaymentTypeRadioControls}
                      disabled={!editable}
                      name='paymentType'
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <br />

          {normalizeBoolean(values.isGateway) && (
            <Fragment>
              <Typography component='label' variant='h6'>
                Integration
              </Typography>

              <Paper style={{ padding: 16 }}>
                <GridFlexEnd size={{ xs: 6 }} alignItems='flex-start'>
                  <Select
                    name='endpoints'
                    label='Endpoints'
                    formControlProps={{ margin: 'none' }}
                    multiple
                    disabled={!editable}
                  >
                    {endpoints.map((item, index) => (
                      <MenuItem value={item.value} key={index}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </GridFlexEnd>
              </Paper>
            </Fragment>
          )}

          <Grid style={{ marginTop: 16 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <ButtonSubmit
                name='save'
                onSubmit={handleSubmit}
                isPending={submitting}
                disabled={!editable}
                type='submit'
              />
            </Box>
          </Grid>
        </form>
      )}
    />
  );
}
